import React, { useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import ScienceIcon from "@mui/icons-material/Science";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ParentsPeace from "./ParentsPeace";
import { motion, AnimatePresence } from "framer-motion";

const programs = [
  {
    num: "01",
    title: "Mathematics",
    icon: <CalculateIcon sx={{ fontSize: 22, color: "#fff" }} />,
    sub: "Algebra · Geometry · Trigonometry · Statistics",
    text: "From number systems and basic algebra to advanced concepts — taught with a focus on logical thinking, real-world problem solving, and exam readiness.",
    topics: ["Algebra & Geometry", "Trigonometry", "Statistics & Probability", "Mensuration"],
    classes: "Classes 6 – 10",
    color: "#3A7CE0", // blue
    tint: "#EAF1FD",
    tintBorder: "#D3E3FA",
    chipBg: "#E7EFFC",
    chipText: "#1F4E9C",
  },
  {
    num: "02",
    title: "Science",
    icon: <ScienceIcon sx={{ fontSize: 22, color: "#fff" }} />,
    sub: "Physics · Chemistry · Biology",
    text: "Hands-on learning through experiments and real-world applications. All three branches covered in depth — from laws of motion to chemical reactions to human anatomy.",
    topics: ["Physics & Motion", "Chemical Reactions", "Life Sciences", "Lab Experiments"],
    classes: "Classes 6 – 10",
    color: "#1D9E75", // teal/green
    tint: "#E7F6EF",
    tintBorder: "#CDEBDE",
    chipBg: "#E1F5EE",
    chipText: "#0F6E56",
  },
  {
    num: "03",
    title: "Languages",
    icon: <MenuBookIcon sx={{ fontSize: 22, color: "#fff" }} />,
    sub: "English · Hindi · Telugu · Tamil · Sanskrit & more",
    text: "Grammar, reading comprehension, essay writing, and verbal communication across 8+ regional and classical languages — tailored to board curriculum.",
    topics: ["Grammar & Writing", "Reading Comprehension", "Spoken Communication", "Literature"],
    classes: "Classes 6 – 10",
    color: "#7F77DD", // purple
    tint: "#F1F0FE",
    tintBorder: "#DCD9F9",
    chipBg: "#EEEDFE",
    chipText: "#3C3489",
  },
];

// Motion variants
const fadeUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const badgesContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const badgeVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const rowsContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const topicsContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07 },
  },
};

const topicVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const MotionBox = motion(Box);

export default function Programs() {
  const [open, setOpen] = useState(0);

  return (
    <>
    <Box sx={{ py: { xs: 7, md: 10 }, background: "linear-gradient(180deg,#fff8f2 0%,#ffffff 100%)" }}>
      <Container maxWidth="xl">

        {/* Header */}
        <MotionBox
          textAlign="center"
          sx={{ mb: 3 }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUpVariants}
        >
          <Typography sx={{ fontSize: { xs: "11px", sm: "12px", md: "13px" }, fontWeight: 700, letterSpacing: { xs: 1, sm: 2 }, color: "#FF6B00", mb: 1 }}>
            OUR PROGRAMS
          </Typography>
          <Typography sx={{ fontSize: { xs: "25px", sm: "30px", md: "34px", lg: "38px" }, fontWeight: 800, color: "#121212", mb: 1 }}>
            Subjects We Teach
          </Typography>
          <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#888", mb: 3 }}>
            Click any subject to explore what's covered
          </Typography>

          {/* Timing badges */}
          <MotionBox
            sx={{ display: "flex", gap: { xs: 1, sm: 1.5 }, justifyContent: "center", flexWrap: "wrap" }}
            variants={badgesContainerVariants}
          >
            {["Morning 7–9 AM", "Evening 4–7 PM", "Weekend Batches"].map((t) => (
              <MotionBox
                key={t}
                variants={badgeVariants}
                sx={{ px: { xs: 1.25, sm: 2 }, py: { xs: 0.65, sm: 0.8 }, borderRadius: "40px", bgcolor: "#FFF4EB", border: "1px solid #FFE5D0" }}
              >
                <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "15px" }, color: "#FF6B00", fontWeight: 700 }}>{t}</Typography>
              </MotionBox>
            ))}
          </MotionBox>
        </MotionBox>

        {/* Accordion list */}
        <MotionBox
          sx={{ borderTop: "1px solid #f0e6de" }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={rowsContainerVariants}
        >
          {programs.map((item, i) => (
            <MotionBox key={i} variants={rowVariants} sx={{ borderBottom: "1px solid #f0e6de" }}>

              {/* Header row */}
              <Box
                onClick={() => setOpen(open === i ? -1 : i)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.5, md: 2 },
                  px: { xs: 1.25, sm: 2.5 },
                  py: 2.2,
                  cursor: "pointer",
                  bgcolor: open === i ? item.tint : "transparent",
                  transition: "background .2s",
                  "&:hover": { bgcolor: open === i ? item.tint : "#faf9f7" },
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 800, color: item.color, opacity: 0.55, minWidth: 24 }}>
                  {item.num}
                </Typography>

                <Box
                  sx={{
                    width: 42, height: 42, borderRadius: "12px",
                    bgcolor: item.color, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: { xs: "16px", sm: "17px", lg: "18px" }, fontWeight: 700, color: "#121212" }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: { xs: "12px", sm: "14px", lg: "15px" }, color: "#aaa", fontWeight: 500 }}>{item.sub}</Typography>
                </Box>

                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{ display: "flex" }}
                >
                  <KeyboardArrowDownIcon sx={{ color: open === i ? item.color : "#ccc" }} />
                </motion.div>
              </Box>

              {/* Expanded body */}
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        pl: { xs: 1.25, sm: "102px" }, pr: { xs: 1.25, sm: 3 }, pb: { xs: 3, md: 5 }, mt: 2,
                        display: "flex", gap: 3, flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
                        <Typography sx={{ fontSize: { xs: "15px", sm: "17px" }, color: "#666", lineHeight: 1.8, mb: 1.5 }}>
                          {item.text}
                        </Typography>
                        <Box sx={{ display: "inline-block", px: 2, py: 0.6, borderRadius: "30px", bgcolor: item.chipBg }}>
                          <Typography sx={{ fontSize: "14px", color: item.chipText, fontWeight: 700 }}>{item.classes}</Typography>
                        </Box>
                      </Box>

                      <MotionBox
                        sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: { xs: "100%", sm: 160 } }}
                        initial="hidden"
                        animate="visible"
                        variants={topicsContainerVariants}
                      >
                        {item.topics.map((t) => (
                          <MotionBox
                            key={t}
                            variants={topicVariants}
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.8,
                              px: 1.5, py: 0.6, borderRadius: "6px",
                              bgcolor: item.chipBg,
                            }}
                          >
                            <Typography sx={{ fontSize: { xs: "13px", sm: "15px" }, color: item.chipText, fontWeight: 600 }}>✓ {t}</Typography>
                          </MotionBox>
                        ))}
                      </MotionBox>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

            </MotionBox>
          ))}
        </MotionBox>

      </Container>
    </Box>
     <ParentsPeace/>
    </>

  );
}